import { useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { useTranslation } from '@mc/i18n'
import { Icon, Tile, TileCaption, TileImage } from '@mc/design-system'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'
import { useStaticData } from '@mc/static-data-react'
import { Video, VideoControlsSmall } from '@mc/video'
import { TrackingParamsProvider } from '@mc/tracking-context'

import CTA from 'components/CTA'
import Image from 'components/LegacyImage'
import { useCourseBySlug } from 'libs/client/api'

import { trackArticlePageClick } from './events'

interface Article {
  featuredCourseSlug: string
}

const client = new QueryClient()

const FeaturedCourseTrailer = () => {
  const [hasPlayed, setHasPlayed] = useState(false)
  const { t } = useTranslation('@mc/next')
  const { article } = useStaticData() as { article: Article }
  const { courseBySlug } = useCourseBySlug(article.featuredCourseSlug)

  if (!courseBySlug) return null

  return (
    <section className='mc-pb-9' data-testid='featured-course-trailer'>
      <div className='row no-gutters mc-bg-gray-700'>
        <div className='col-12 col-sm-6 mc-text--center mc-text-sm--left'>
          <div className='mc-p-8'>
            <h2 className='mc-text-h4'>{t('article.courseGem.title')}</h2>
            <div className='d-none d-sm-block'>
              <CTA className='mc-mt-6' onClick={trackArticlePageClick} />
            </div>
          </div>
        </div>
        <div className='col-12 col-sm'>
          {hasPlayed ? (
            <QueryClientProvider client={client}>
              <TrackingParamsProvider
                value={{
                  content_id:
                    courseBySlug.brightcoveVideoId ?? courseBySlug.mediaUuid,
                  content_type: TRACK_CONTENT_TYPES.MARKETING,
                  content_title: courseBySlug.title,
                  course_id: courseBySlug.id,
                }}
              >
                <Video
                  videoId={courseBySlug.brightcoveVideoId}
                  mediaUuid={courseBySlug.mediaUuid}
                  loop
                  playsInline
                  autoPlay
                >
                  <VideoControlsSmall />
                </Video>
              </TrackingParamsProvider>
            </QueryClientProvider>
          ) : (
            <Tile onClick={() => setHasPlayed(true)}>
              <TileCaption position='center center'>
                <Icon kind='play-circle' className='mc-icon--8' />
              </TileCaption>
              <TileImage
                image={
                  <Image
                    alt=''
                    layout='fill'
                    src={courseBySlug.image16x9 || ''}
                  />
                }
              />
            </Tile>
          )}
        </div>
        <div className='d-block d-sm-none'>
          <div className='col-12 mc-text--center'>
            <CTA className='mc-my-8' onClick={trackArticlePageClick} />
          </div>
        </div>
      </div>
    </section>
  )
}

export default FeaturedCourseTrailer
